import React, { useState } from 'react'
import { screenSizes } from 'shared/RandomUtils'
import useResizeListener from 'shared/Hooks/useResizeListener'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import styled from 'styled-components'
import EmployeeOnboardingView from 'features/Onboarding/employee/EmployeeOnboardingView'
import { isOnboarding } from 'features/Onboarding/helperFunctions'

// New dashboard components
import MyWalletSection from '../components/MyWalletSection'
import ThisMonthStatsSection from '../components/ThisMonthStats'
import MyProgramsSection from '../components/MyProgramsSection'

const Container = styled.div`
  padding: 1.25rem;
  max-width: 1400px;
  margin: 0 auto;
`

const AlertContainer = styled.div`
  margin-bottom: 1rem;
`

const AlertContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.5rem 1rem;
  background-color: ${(props) => props.theme.palette.accent.blue100};
  border: 1px solid ${(props) => props.theme.palette.accent.blue200};
  border-radius: 0.5rem;
`

const AlertText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${(props) => props.theme.palette.text.primary};
  font-size: 0.875rem;
`

const AlertIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 50%;
  background-color: ${(props) => props.theme.palette.accent.blue500};
  color: white;
  flex-shrink: 0;
`

const InfoIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 16V12M12 8H12.01'
      stroke='currentColor'
      strokeWidth='3'
      strokeLinecap='round'
    />
  </svg>
)

const ReloadButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${(props) => props.theme.palette.accent.blue500};
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${(props) => props.theme.palette.accent.blue600};
  }
`

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints[2]}) {
    grid-template-columns: 1fr;
  }
`

const BottomRow = styled.div`
  width: 100%;
`

const EmployeeDashboardView = React.memo(() => {
  const onboarding = useSelector((state: AppState) => state.onboarding)

  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= screenSizes.mobile
  )
  const handleResize = React.useCallback(
    (width: number) => {
      setIsMobile(width <= screenSizes.mobile)
    },
    [setIsMobile]
  )
  useResizeListener(() => handleResize(window.innerWidth), [])

  // Mock low balance state for demo (Clipper card balance < $10)
  const showLowBalanceAlert = true
  const clipperBalance = 45.5

  if (isOnboarding(onboarding)) {
    return <EmployeeOnboardingView />
  }

  return (
    <Container>
      {showLowBalanceAlert && (
        <AlertContainer>
          <AlertContent>
            <AlertText>
              <AlertIconWrapper>
                <InfoIcon />
              </AlertIconWrapper>
              Your Clipper balance is low. Reload to avoid service interruption.
            </AlertText>
            <ReloadButton onClick={() => console.log('Reload clicked')}>
              Reload Now
            </ReloadButton>
          </AlertContent>
        </AlertContainer>
      )}

      <TopRow>
        <MyWalletSection />
        <ThisMonthStatsSection />
      </TopRow>
      <BottomRow>
        <MyProgramsSection />
      </BottomRow>
    </Container>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  EmployeeDashboardView.displayName = 'EmployeeDashboardView'
}

export default EmployeeDashboardView
